/**
 * Projects a pending proposal onto a record, producing what the record will
 * look like once the proposal is applied.
 *
 * ## Why a projection rather than a changeset
 *
 * The record surface renders a `CrmObject`. If the projected result IS a
 * `CrmObject`, every section, row, icon, ordering rule and value renderer the
 * real record already has works unchanged — the draft is not a different kind
 * of thing, it is the same record shown one step ahead. The only extra
 * information a draft carries is *what the values used to be*, which travels
 * separately (`previousValues`) so that nothing downstream has to understand
 * proposals to render one.
 *
 * ## The two shapes
 *
 * - **Creating** — there is no base. The projected object is synthetic and the
 *   `previousValues` map is empty, so the surface renders it as a plain record
 *   with no before/after chrome. There is no "before".
 * - **Editing** — the base is the live record, the overlay is merged on top,
 *   and `previousValues` holds the prior value for overlaid keys ONLY. That
 *   emptiness is what keeps untouched properties rendering plainly instead of
 *   as no-op diffs.
 *
 * ## This never touches the CRM store
 *
 * A synthetic object must not be upserted into `crmObjectStore`. It would be
 * marked complete, which stops the slideout ever fetching the real record; its
 * `last_event_seq` would lose a merge race against the genuine server response;
 * and the first real websocket event would land as a seq gap, invalidating
 * every list and detail query for the type. The projection is handed to the
 * record surface (`RecordSurfaceProvider` → `RecordViewProvider`) and its
 * prior values read through `RecordDraftContext` — it has no store presence
 * at all.
 */

import type {
  AssociationDetail,
  CrmObject,
  EntityTypeName,
  PropertyValue,
} from '@/types/crm';

export interface ProjectedRecord {
  /** The record as it will be once the proposal is applied. */
  projectedObject: CrmObject;
  /**
   * Prior values, keyed by property name, for the properties the proposal
   * changes — and only those. A key is absent when the property is untouched
   * or when the proposal sets it to the value it already had, so consumers can
   * treat "present in this map" as "render a before/after".
   */
  previousValues: Record<string, unknown>;
  /**
   * What the proposal does to each association target, keyed by
   * `target_entity_id`. Only targets the proposal changes appear here — an
   * association the record already has and keeps is absent, so consumers can
   * treat "present in this map" as "mark this one".
   *
   * Deliberately NOT modelled as a replacement: a record can legitimately hold
   * several associations of the same type, so an addition does not imply the
   * existing one goes away. Marking each target with its own fate is the
   * honest rendering — a move shows as one removed + one added, while a pure
   * addition shows only the added marker.
   */
  associationChanges: Record<string, AssociationChange>;
}

/** What a proposal does to a single association target. */
export type AssociationChange = 'added' | 'removed';

export interface BuildProjectedObjectArgs {
  /** The live record, absent while creating. */
  base: CrmObject | undefined;
  /** Proposed values keyed by CRM property name, already unwrapped. */
  overlay: Record<string, unknown>;
  /** Required when there is no base — the type of the thing being created. */
  entityTypeName: string | undefined;
  /** Required when there is no base — the id minted by the agent's transform(). */
  entityId: string | undefined;
  orgId: number;
  /**
   * Association overlays to merge onto the projected object, keyed by nothing
   * (a flat list) since a record can carry several associations of the same
   * type. The caller has already resolved each target — this function never
   * looks one up — so these are ready to attach as-is.
   */
  associationOverlays?: {
    association_type_name: string;
    target_entity_id: string;
  }[];
  /**
   * Associations the proposal removes. They stay in the projected object's
   * association list so the surface can render them struck through — dropping
   * them would make a removal indistinguishable from nothing happening.
   */
  removedAssociationOverlays?: {
    association_type_name: string;
    target_entity_id: string;
  }[];
}

/**
 * `last_event_seq` for a record that does not exist yet.
 *
 * There is no honest "not yet" value, and 0 is the only defensible choice: it
 * is what `createEventBuilder` turns into `seq: 1`, which is exactly what a
 * genuine creation needs. It is deliberately NOT a high sentinel — a high value
 * would make real websocket events look already-applied and be silently
 * dropped.
 */
const UNSAVED_SEQ = 0;

function areEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  // Structural compare for the object/array values CRM properties can hold
  // (multi-value chips, JSON config). Cheap here: these maps are per-record and
  // only the overlaid keys reach this path.
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function buildProjectedObject({
  base,
  overlay,
  entityTypeName,
  entityId,
  orgId,
  associationOverlays,
  removedAssociationOverlays,
}: BuildProjectedObjectArgs): ProjectedRecord | undefined {
  const resolvedTypeName = base?.entity_type_name ?? entityTypeName;
  if (!resolvedTypeName) {
    // Without a type there is no schema, so there are no fields to lay out and
    // nothing meaningful to render. The caller shows its own empty state.
    return undefined;
  }
  // The type is known before the id is. A builder opens on "create a validation
  // rule" and only receives an id once the agent forms its first proposal, so
  // this placeholder covers that window and lets the surface render the type's
  // shape instead of nothing. It is never used to fetch: the sections that key
  // on an entity id are switched off while the record is unsaved.
  const resolvedEntityId =
    base?.entity_id ?? entityId ?? `draft:${resolvedTypeName}`;

  const baseProperties = base?.properties ?? {};
  const projectedProperties: Record<string, PropertyValue> = {
    ...baseProperties,
  };
  const previousValues: Record<string, unknown> = {};

  // Fresh objects, never the caller's array or its elements — the projected
  // object must not point back into provider internals.
  const overlayAssociations: AssociationDetail[] = (
    associationOverlays ?? []
  ).map(a => ({
    association_type_name: a.association_type_name,
    target_entity_id: a.target_entity_id,
  }));

  // Change markers, keyed by target id. Additions the record ALREADY has are
  // not changes — re-asserting an existing association marks nothing. A
  // record that does not exist yet has no prior state to diff against: every
  // association on it is new by definition, so marking each one would just be
  // noise (mirrors `previousValues`, which is likewise only recorded when
  // there is a `base`).
  const baseTargetIds = new Set(
    (base?.associations ?? []).map(a => a.target_entity_id)
  );
  const associationChanges: Record<string, AssociationChange> = {};
  if (base) {
    for (const a of overlayAssociations) {
      if (!baseTargetIds.has(a.target_entity_id)) {
        associationChanges[a.target_entity_id] = 'added';
      }
    }
  }
  // Removals of associations the record does not have are no-ops; only mark
  // what is actually there to take away. The association itself needs no
  // special handling — it is already on `base.associations`, which the
  // projected object spreads through, so marking it is enough to render it
  // struck through rather than silently dropping it.
  for (const a of removedAssociationOverlays ?? []) {
    if (baseTargetIds.has(a.target_entity_id)) {
      associationChanges[a.target_entity_id] = 'removed';
    }
  }

  for (const [name, proposedValue] of Object.entries(overlay)) {
    const priorValue = baseProperties[name]?.value;
    // Setting a property to what it already holds is not a change. Recording it
    // would render `X → X`, which reads as an edit the user did not ask for.
    if (base && !areEqual(priorValue, proposedValue)) {
      previousValues[name] = priorValue;
    }
    projectedProperties[name] = {
      // Preserve the existing metadata (source, timestamps) and replace only
      // the value, so a projected row still renders whatever provenance the
      // real record had.
      ...baseProperties[name],
      value: proposedValue,
    };
  }

  const projectedObject: CrmObject = base
    ? {
        ...base,
        properties: projectedProperties,
        ...(overlayAssociations.length > 0
          ? {
              associations: [
                ...(base.associations ?? []),
                ...overlayAssociations,
              ],
            }
          : {}),
      }
    : {
        // Synthetic, for a record that does not exist yet. `entity_id` is not a
        // placeholder: the agent mints it once in `transform()`, freezes it,
        // and it IS the id the entity receives when the proposal executes — so
        // this object and the saved record share a key, and the surface does
        // not remount when the record becomes real.
        id: resolvedEntityId,
        entity_id: resolvedEntityId,
        org_id: orgId,
        entity_type_name: resolvedTypeName as EntityTypeName,
        properties: projectedProperties,
        last_event_seq: UNSAVED_SEQ,
        // Honest: a record that was never created was never deleted.
        deleted_at: null,
        // Deliberately no `associations` by default. An empty/absent list is
        // what keeps `useAssociations` from trying to resolve association
        // targets for an entity the server has never seen. Association
        // overlays are the exception: the caller has already resolved each
        // target, so including them is safe — only add the key when there is
        // something to add.
        ...(overlayAssociations.length > 0
          ? { associations: overlayAssociations }
          : {}),
      };

  return { projectedObject, previousValues, associationChanges };
}
